import React, { useState, useRef, ReactNode } from 'react';

import { AlertProps } from '@ast/magma/components/alert';

import { createGenericContext } from '@app/common/contexts/createGenericContext';

export interface Alert extends AlertProps {
  readonly title?: string;
  readonly message?: ReactNode;
  readonly withHtmlMarkup?: boolean;
}

export interface NotifierContext {
  readonly alerts: Array<Alert>;
  readonly addAlerts: (alerts: Array<Alert>) => void;
  readonly removeAlert: (index: number) => void;
  readonly clearAlerts: () => void;
  readonly setQueue: (queue: Alert[]) => void;
  readonly resolveQueue: () => void;
}

/**
 * Context for alerts notification.
 */
const [
  useNotifierContext,
  ContextProvider,
] = createGenericContext<NotifierContext>();

/**
 * Notification alerts context provider.
 */
const NotifierProvider: React.FC = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const hasAlerts = useRef(false);

  // Due to queue is mutable we can manage state in react cleanup callback
  // Also it will be useful between rerender transitions
  const alertsQueue = useRef<Alert[] | null>(null);

  const addAlerts = (items: Array<Alert>) => {
    setAlerts((state) => {
      const result = [
        ...state,
        ...items.map((item: Alert) => ({ ...item })),
      ];
      hasAlerts.current = result.length > 0;
      return result;
    });
  };

  const removeAlert = (index: number) => {
    setAlerts((state) => {
      const result = [...state];

      result.splice(index, 1);
      hasAlerts.current = result.length > 0;
      return result;
    });
  };

  const clearAlerts = () => {
    if (hasAlerts.current) {
      hasAlerts.current = false;
      setAlerts([]);
    }
  };

  // Set batch of alerts to be queued
  const setQueue = (queue: Alert[]) => {
    alertsQueue.current = queue;
  };

  // Resolve whole queue of alerts
  const resolveQueue = () => {
    if (alertsQueue?.current?.length) {
      addAlerts(alertsQueue.current);
      alertsQueue.current = null;
    }
  };

  return (
    <ContextProvider value={{
      alerts,
      addAlerts,
      removeAlert,
      clearAlerts,
      setQueue,
      resolveQueue,
    }}
    >
      {children}
    </ContextProvider>
  );
};

export { useNotifierContext, NotifierProvider };
